import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  resourceCapabilitiesApi,
  type CreateResourceMethodPayload,
  type MethodDefinitionPayload,
  type CreateResourceProcedurePayload,
  type UpdateResourceMethodPayload,
  type UpdateResourceProcedurePayload,
} from '@/api/resourceCapabilitiesApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * The resource capability matrix behind Configure → Method.
 *
 * Every key carries `selectedTenantId`: the matrix is one tenant's answer to who
 * can do what, so a switch must land on a cold key rather than serving the
 * previous tenant's rows while it refetches.
 */
const key = (tenantId: string | null, part: string) =>
  ['resource-capabilities', tenantId, part] as const

/**
 * Resources, transplant templates, methods and process steps for the pickers —
 * one request.
 *
 * Always refetched on mount, unlike everything else here.
 *
 * This list is DERIVED from data owned by other parts of the app: the process
 * steps come from form definitions, the procedures from templates. Nothing that
 * creates those touches the `resource-capabilities` key, so the matrix's own
 * invalidation cannot keep it fresh — and with the client's 5-minute
 * `staleTime` and `refetchOnWindowFocus: false`, authoring a form and walking
 * straight to Configure -> Method showed a list drawn before the form existed.
 * The new process was simply missing, with nothing to suggest the list was old.
 *
 * Refetching on mount rather than invalidating from the form builder: the
 * dependency runs the wrong way for that. Every future surface that creates a
 * process step would have to remember to invalidate a matrix it has no reason
 * to know about, which is exactly the kind of rule that gets forgotten. Opening
 * the tab re-reads instead — one request, on a page that is not hot.
 */
export function useCapabilityOptions() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'options'),
    queryFn: () => resourceCapabilitiesApi.options(tenantId as string),
    enabled: !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/** The Resource × Procedure grid, with create / update / delete. */
export function useResourceProcedures() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()

  // Invalidate the whole matrix, not just this grid: both grids resolve resource
  // names from the same rows, and the options endpoint counts nothing that a
  // write here changes — but a stale name in the other grid is still a lie.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['resource-capabilities', tenantId] })
  }

  const list = useQuery({
    queryKey: key(tenantId, 'procedures'),
    queryFn: () => resourceCapabilitiesApi.listProcedures(tenantId as string),
    enabled: !!tenantId,
  })

  const create = useMutation({
    mutationFn: (payload: CreateResourceProcedurePayload) =>
      resourceCapabilitiesApi.createProcedure(tenantId as string, payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: UpdateResourceProcedurePayload }) =>
      resourceCapabilitiesApi.updateProcedure(tenantId as string, input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => resourceCapabilitiesApi.removeProcedure(tenantId as string, id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}

/** The Resource × Method grid, with create / update / delete. */
export function useResourceMethods() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['resource-capabilities', tenantId] })
  }

  const list = useQuery({
    queryKey: key(tenantId, 'methods'),
    queryFn: () => resourceCapabilitiesApi.listMethods(tenantId as string),
    enabled: !!tenantId,
  })

  const create = useMutation({
    mutationFn: (payload: CreateResourceMethodPayload) =>
      resourceCapabilitiesApi.createMethod(tenantId as string, payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: UpdateResourceMethodPayload }) =>
      resourceCapabilitiesApi.updateMethod(tenantId as string, input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => resourceCapabilitiesApi.removeMethod(tenantId as string, id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}

/**
 * The METHODS themselves — the collections of process steps.
 *
 * Separate from `useResourceMethods`, which is who is qualified in what. Both
 * invalidate the whole matrix on a write: renaming a method or changing its
 * steps changes what the qualification grid reads.
 */
export function useMethodDefinitions() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['resource-capabilities', tenantId] })
  }

  const list = useQuery({
    queryKey: key(tenantId, 'method-definitions'),
    queryFn: () => resourceCapabilitiesApi.listMethodDefinitions(tenantId as string),
    enabled: !!tenantId,
  })

  const create = useMutation({
    mutationFn: (payload: MethodDefinitionPayload) =>
      resourceCapabilitiesApi.createMethodDefinition(tenantId as string, payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: Partial<MethodDefinitionPayload> }) =>
      resourceCapabilitiesApi.updateMethodDefinition(tenantId as string, input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) =>
      resourceCapabilitiesApi.removeMethodDefinition(tenantId as string, id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}

/**
 * One process step's methods — the step side of the same membership.
 *
 * Its own hook rather than a branch of `useMethodDefinitions`: this one is keyed
 * by field id, and the Field Attributes editor opens on one step at a time. A
 * write invalidates the whole matrix, because the Methods view and the
 * qualification grid both read what it changed.
 */
export function useStepMethods(fieldId: string) {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()

  const list = useQuery({
    queryKey: [...key(tenantId, 'process-steps'), fieldId, 'methods'],
    queryFn: () => resourceCapabilitiesApi.listStepMethods(tenantId as string, fieldId),
    enabled: !!tenantId && !!fieldId,
  })

  const save = useMutation({
    mutationFn: (methodIds: string[]) =>
      resourceCapabilitiesApi.setStepMethods(tenantId as string, fieldId, methodIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['resource-capabilities', tenantId] })
    },
  })

  return { list, save }
}
